/* =========================================================================
   RRC Schedule helpers — ports the prototype's `CITIES` logic to vanilla JS
   on the Shopify side. Reads the JSON blob from <script id="rrc-schedule-data">
   that snippets/rrc-schedule-data.liquid emits.

   Public API on `window.RRC`:
     RRC.cities                 → { gainesville: {...}, tampa: {...} }
     RRC.parseTime(str)         → { hour, min } | null
     RRC.ymdKey(date)           → "YYYY-MM-DD"
     RRC.sameYMD(a, b)          → bool
     RRC.isPaused(run, date)    → bool
     RRC.isSkipped(run, date)   → bool
     RRC.eventOnDate(city, d)   → bool
     RRC.nextRunFor(city, now)  → { city, run, date, isLaunch, isEvent } | null
     RRC.nextOverall(now)       → soonest run across all cities
     RRC.shortLabel(run)        → tight label (e.g. "SNL")
     RRC.formatCountdown(diffMs)→ "2d 4h 30m" | "1h 12m" | "5m" | "Now"
   ========================================================================= */
(function () {
  var dataEl = document.getElementById('rrc-schedule-data');
  if (!dataEl) {
    console.warn('[RRC] No schedule data found — make sure rrc-schedule-data snippet is rendered.');
    return;
  }
  var raw;
  try { raw = JSON.parse(dataEl.textContent); }
  catch (e) { console.error('[RRC] Schedule JSON parse error:', e); return; }

  // Coerce ISO date strings into Date objects on each city/run.
  var cities = raw.cities || {};
  Object.keys(cities).forEach(function (key) {
    var city = cities[key];
    if (city.launchDate && typeof city.launchDate === 'string') {
      city.launchDate = isoToLocalMidnight(city.launchDate);
    }
    (city.runs || []).forEach(function (run) {
      if (run.pausedUntil && typeof run.pausedUntil === 'string') {
        run.pausedUntil = isoToLocalMidnight(run.pausedUntil);
      }
    });
    (city.events || []).forEach(function (ev) {
      if (typeof ev.date === 'string') ev.date = isoToLocalMidnight(ev.date);
    });
  });

  function isoToLocalMidnight(s) {
    // 'YYYY-MM-DD' → local midnight Date (avoids UTC drift that would shift days)
    var parts = s.split('-').map(Number);
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

  function parseTime(timeStr) {
    if (!timeStr || timeStr === 'TBA') return null;
    var m = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!m) return null;
    var hour = parseInt(m[1], 10);
    var min  = parseInt(m[2], 10);
    if (m[3].toUpperCase() === 'PM' && hour !== 12) hour += 12;
    if (m[3].toUpperCase() === 'AM' && hour === 12) hour = 0;
    return { hour: hour, min: min };
  }

  function ymdKey(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }
  function sameYMD(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }
  function isPaused(run, date)  { return run.pausedUntil && date < run.pausedUntil; }
  function isSkipped(run, date) { return (run.skipDates || []).indexOf(ymdKey(date)) !== -1; }
  function eventOnDate(city, date) {
    return (city.events || []).some(function (ev) { return sameYMD(ev.date, date); });
  }
  function shortLabel(run) {
    return run.abbr || (run.label || '').replace(/ Run$/, '');
  }

  function eventAsRun(city, ev) {
    return {
      city: city,
      run: {
        dayName: ev.date.toLocaleDateString('en-US', { weekday: 'long' }),
        label: ev.label,
        meet: ev.meet, start: ev.start, tone: ev.tone, pillColor: ev.pillColor,
        location: ev.location, distances: ev.distances,
        partners: ev.partners,
        // Opt-in: only events with an explicit "special": true in the schedule
        // data get the ★ / "Special Event" treatment. Regular one-off events
        // (Coffee Runs at rotating shops, SNLs at different parks, etc.) are
        // in events[] to override the recurring template on that date but
        // shouldn't be labeled "special".
        special: ev.special === true
      },
      date: (function () { var d = new Date(ev.date); d.setHours(0,0,0,0); return d; })(),
      isLaunch: false,
      isEvent: true
    };
  }

  function nextRunFor(city, now) {
    if (city.comingSoon && city.launchDate && now < city.launchDate) {
      var today = new Date(now); today.setHours(0,0,0,0);
      var launch = new Date(city.launchDate); launch.setHours(0,0,0,0);
      return {
        city: city,
        run: (city.runs && city.runs[0]) || {},
        date: launch,
        isLaunch: true,
        diff: Math.round((launch - today) / 86400000)
      };
    }

    var startOfToday = new Date(now); startOfToday.setHours(0,0,0,0);
    var cands = [];

    // 1. Upcoming events (treated as one-off runs)
    (city.events || []).forEach(function (ev) {
      if (ev.canceled === true) return; // canceled events don't compete as "next run"
      var evDate = new Date(ev.date); evDate.setHours(0,0,0,0);
      if (evDate >= startOfToday) {
        // For today's event, only include if meet time hasn't passed
        if (sameYMD(evDate, startOfToday)) {
          var t = parseTime(ev.meet);
          if (t) {
            var meetMs = new Date(evDate.getFullYear(), evDate.getMonth(), evDate.getDate(), t.hour, t.min).getTime();
            if (now.getTime() < meetMs) cands.push(eventAsRun(city, ev));
          } else {
            cands.push(eventAsRun(city, ev));
          }
        } else {
          cands.push(eventAsRun(city, ev));
        }
      }
    });

    // 2. Recurring runs in the next 9 weeks
    (city.runs || []).forEach(function (run) {
      for (var w = 0; w < 9 * 7; w++) {
        var d = new Date(startOfToday); d.setDate(d.getDate() + w); d.setHours(0,0,0,0);
        if (d.getDay() !== run.dayNum) continue;
        if (isPaused(run, d)) continue;
        if (isSkipped(run, d)) continue;
        if (eventOnDate(city, d)) continue; // event takes over this date
        // Same-day: must be before meet time
        if (sameYMD(d, startOfToday)) {
          var t = parseTime(run.meet);
          if (t) {
            var meetMs = new Date(d.getFullYear(), d.getMonth(), d.getDate(), t.hour, t.min).getTime();
            if (now.getTime() >= meetMs) continue;
          }
        }
        cands.push({ city: city, run: run, date: d, isLaunch: false, isEvent: false });
        break; // only the soonest occurrence of each recurring run
      }
    });

    // Pick soonest by date+meet
    cands.sort(function (a, b) {
      var ta = a.date.getTime() + (parseTime(a.run.meet) ? (parseTime(a.run.meet).hour * 3600000 + parseTime(a.run.meet).min * 60000) : 0);
      var tb = b.date.getTime() + (parseTime(b.run.meet) ? (parseTime(b.run.meet).hour * 3600000 + parseTime(b.run.meet).min * 60000) : 0);
      return ta - tb;
    });
    return cands[0] || null;
  }

  function nextOverall(now) {
    var all = Object.keys(cities).map(function (k) { return nextRunFor(cities[k], now); }).filter(Boolean);
    all.sort(function (a, b) {
      var ta = a.date.getTime() + (parseTime(a.run.meet) ? (parseTime(a.run.meet).hour * 3600000 + parseTime(a.run.meet).min * 60000) : 0);
      var tb = b.date.getTime() + (parseTime(b.run.meet) ? (parseTime(b.run.meet).hour * 3600000 + parseTime(b.run.meet).min * 60000) : 0);
      return ta - tb;
    });
    return all[0] || null;
  }

  function formatCountdown(diffMs) {
    if (diffMs <= 0) return 'Now';
    var totalMin = Math.floor(diffMs / 60000);
    var d = Math.floor(totalMin / 1440);
    var h = Math.floor((totalMin % 1440) / 60);
    var m = totalMin % 60;
    if (d > 0) return d + 'd ' + h + 'h ' + m + 'm';
    if (h > 0) return h + 'h ' + m + 'm';
    return m + 'm';
  }

  window.RRC = {
    cities: cities,
    parseTime: parseTime,
    ymdKey: ymdKey,
    sameYMD: sameYMD,
    isPaused: isPaused,
    isSkipped: isSkipped,
    eventOnDate: eventOnDate,
    shortLabel: shortLabel,
    nextRunFor: nextRunFor,
    nextOverall: nextOverall,
    formatCountdown: formatCountdown
  };
  document.dispatchEvent(new CustomEvent('rrc:schedule-ready'));
})();
